import Pill from 'ext-ui/components/Pill/Pill'
import React from 'react'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import {
  formatStatementAmount,
  formatStatementDate,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatements.format'
import type {
  NextPaymentDue,
  StatementPayment,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import * as style from 'src/page-components/Accounts/AccountStatementsTab/components/StatementPayments/StatementPayments.style'
import {
  paymentStatusPillColor,
  usePaymentMethodLabel,
  usePaymentStatusLabel,
} from 'src/page-components/Accounts/AccountStatementsTab/useStatementLabels'

interface StatementPaymentsProps {
  /** `null` when nothing is due — a real answer, not a missing one. */
  nextPaymentDue: NextPaymentDue | null
  payments: StatementPayment[]
}

/**
 * One payment-history row.
 *
 * Split out because the method and status labels come from hooks, which cannot
 * be called inside the `.map` below.
 *
 * The status is rendered from the record rather than assumed: the contract's
 * history includes `PENDING`, `FAILED`, and `REVERSED` records alongside
 * `PAID`, so a hardcoded "Paid" pill would label a failed payment as settled.
 * The row's supporting line is worded to match — "Paid ${date}" only reads
 * correctly for a payment that actually was.
 */
const PaymentHistoryRow: React.FC<{ payment: StatementPayment }> = ({
  payment,
}) => {
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()
  const methodLabel = usePaymentMethodLabel(payment.method)
  const statusLabel = usePaymentStatusLabel(payment.status)

  return (
    <li css={style.historyRow}>
      <div>
        <div css={style.historyAmount}>
          {formatStatementAmount(formatCurrency, payment.amount)}
        </div>
        <div css={style.historyDetail}>
          {payment.status === 'PAID'
            ? translate('Paid ${paidOn} · ${method}', {
                paidOn: formatStatementDate(payment.paidOn),
                method: methodLabel,
              })
            : translate('${paidOn} · ${method}', {
                paidOn: formatStatementDate(payment.paidOn),
                method: methodLabel,
              })}
        </div>
      </div>
      {/* Status is spelled out, never colour-only. */}
      <Pill color={paymentStatusPillColor(payment.status)} size="small">
        {statusLabel}
      </Pill>
    </li>
  )
}

/**
 * Payments section of the card-detail Statements tab (EX-40600).
 *
 * Strictly read-only, and that is a design guardrail rather than a gap: Extend
 * has no bank-payment capability, so there is deliberately NO pay / pay-off
 * action anywhere in this section. It shows the next amount due and the prior
 * payments, nothing more. Do not add a Pay control here without the capability
 * behind it.
 */
const StatementPayments: React.FC<StatementPaymentsProps> = ({
  nextPaymentDue,
  payments,
}) => {
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()

  return (
    <section data-testid="statementPayments">
      <div css={style.nextPayment}>
        <div>
          <p css={style.nextPaymentLabel}>{translate('Next payment due')}</p>
          {/*
            The settled state is rendered, not substituted. `null` here means
            the summary said nothing is due, which is a real answer — showing a
            fabricated balance for it (as this section briefly did) states money
            owed on a card that owes none.
          */}
          {nextPaymentDue === null ? (
            <p css={style.nextPaymentFigure}>
              <span css={style.nextPaymentAmount}>
                {translate('Nothing due')}
              </span>
            </p>
          ) : (
            <p css={style.nextPaymentFigure}>
              <span css={style.nextPaymentAmount}>
                {formatStatementAmount(formatCurrency, nextPaymentDue.amount)}
              </span>
              <span css={style.nextPaymentDue}>
                {translate('due ${dueDate}', {
                  dueDate: formatStatementDate(nextPaymentDue.dueOn),
                })}
              </span>
              {/*
                Spelled out, never colour alone. Taken from the server's
                `status`, never from comparing the due date to the browser
                clock — that boundary is a billing-calendar date in the card's
                own timezone.
              */}
              {nextPaymentDue.isPastDue && (
                <Pill color="severe" size="small">
                  {translate('Past due')}
                </Pill>
              )}
            </p>
          )}
        </div>
      </div>

      <h3 css={style.historyHeading}>{translate('Payment history')}</h3>
      <ul css={style.historyList}>
        {payments.map((payment) => (
          <PaymentHistoryRow key={payment.id} payment={payment} />
        ))}
      </ul>
    </section>
  )
}

export default StatementPayments
